import React, { useEffect, useMemo, useRef, useState } from 'react';
import DrumRoll from '../drumroll/DrumRoll';
import './DatePicker.css';
import { formatDate } from '../../utils/formatDate';
import { isMobile } from '../../utils/isMobile';

type Props = {
  min?: string | Date;
  max?: string | Date;
  value?: string;
  initValue? : string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

export const DatePicker: React.FC<Props> = ({min = '1900-01-01', max = '2100-12-31', onChange, ...props}) => {
  const [value, setValue] = useState(props.value || (isMobile() ? props.initValue : '') || '');
  const valueRef = useRef<string>(value);
  valueRef.current = value;

  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (!props.value) return;
    setValue(props.value);
  }, [props.value]);

  useEffect(() => {
    if (!focus) return;
    const handleClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('.date-picker') != null) return;
      setFocus(false);
      const value = valueRef.current;
      if (onChange != null && value != null) {
        onChange(value);
      }
    }
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [focus, onChange]);

  const {year, month, day} = toFragment(value);
  const {year: minYear, month: minMonth, day: minDay} = toFragment(min);
  const {year: maxYear, month: maxMonth, day: maxDay} = toFragment(max);

  const years = useMemo(() => range(minYear, maxYear).map(y => ({value: y, label: `${y}年`})), [minYear, maxYear]);
  const months = useMemo(() => {
    return getMonthRange(year, minYear, maxYear, minMonth, maxMonth).map(m => ({value: m, label: `${String(m).padStart(2, ' ')}月`}))
  }, [year, minYear, maxYear, minMonth, maxMonth]);
  const days = useMemo(() => {
    return getDayRange(year, month, minYear, maxYear, minMonth, maxMonth, minDay, maxDay).map(d => ({value: d, label: `${String(d).padStart(2, ' ')}日 (${toWday(year, month, d)})`}))
  }, [year, minYear, maxYear, month, minMonth, maxMonth, minDay, maxDay]);

  const handleYearChange = (y: number, doChange = false) => {
    const date = determineDate({year, month, day}, {year: y, month, day}, min, max);
    setValue(date);
    if (doChange) {
      onChange?.(date);
    }
  }
  const handleMonthChange = (m: number, doChange = false) => {
    const date = determineDate({year, month, day}, {year, month: m, day}, min, max);
    setValue(date);
    if (doChange) {
      onChange?.(date);
    }
  }
  const handleDayChange = (d: number, doChange = false) => {
    const date = determineDate({year, month, day}, {year, month, day: d}, min, max);
    setValue(date)
    if (doChange) {
      onChange?.(date);
    }
  }


  if (!isMobile()) {
    return <div className="date-picker">
      <div className="date-picker-select">
        <select value={value !== '' ? String(year ?? '') : ''} onChange={e => handleYearChange(parseInt(e.target.value), true)}>
          {value === '' && <option value={''}></option>}
          {years.map(year => <option key={year.value} value={year.value}>{year.label}</option>)}
        </select>
        <select value={value !== '' ? String(month ?? '') : ''} onChange={e => handleMonthChange(parseInt(e.target.value), true)}>
          {value === '' && <option value={''}></option>}
          {months.map(month => <option key={month.value} value={month.value}>{month.label}</option>)}
        </select>
        <select value={value !== '' ? String(day ?? '') : ''} onChange={e => handleDayChange(parseInt(e.target.value), true)}>
          {value === '' && <option value={''}></option>}
          {days.map(day => <option key={day.value} value={day.value}>{day.label}</option>)}
        </select>
      </div>
    </div>
  }

  return <div className="date-picker">
    <div className="date-picker-input">
      <input type="text" readOnly={true} value={toDisplayDate(props.value)} placeholder={props.placeholder} onFocus={() => setFocus(true)}/>
    </div>
    {focus && <div className="date-picker-popup">
      <div className="date-picker-inner">
        <DrumRoll options={years} value={year} onChange={handleYearChange}/>
        <DrumRoll options={months} value={month} onChange={handleMonthChange}/>
        <DrumRoll options={days} value={day} onChange={handleDayChange}/>
      </div>
    </div>}
  </div>
}

function toFragment(value: string | Date) {
  value = value instanceof Date ? formatDate(value) : value;
  const [year , month, day] = value.split('-').map(f => parseInt(f));
  const now = new Date();
  return {
    year: year != null && !Number.isNaN(year) ? year : now.getFullYear(),
    month: month != null && !Number.isNaN(month) ? month : now.getMonth() + 1,
    day: day != null && !Number.isNaN(day) ? day : now.getDate(),
  };
}

function range(start: number, end: number) {
  const ret = [];
  for (let i = start; i <= end; i++) {
    ret.push(i);
  }
  return ret;
}

function toDateString(y: number, m: number, d: number) {
  const date = new Date(y, m - 1, d);
  return formatDate(date);
}

function toDisplayDate(date: string | null | undefined) {
  if (date == null || date === '') return '';
  const {year, month, day} = toFragment(date);
  return `${year}年${month}月${day}日 (${toWday(year, month, day)})`;
}

function toWday(y: number, m: number, d: number) {
  const date = new Date(y, m - 1, d);
  const day = date.getDay()
  return (['日', '月', '火', '水', '木', '金', '土'] as const)[day];
}

function getMonthRange(year: number, minYear: number, maxYear: number, minMonth: number, maxMonth: number) {
  if (year === minYear) return range(minMonth, 12);
  if (year === maxYear) return range(1, maxMonth);
  return range(1, 12);
}

function getDayRange(year: number, month: number, minYear: number, maxYear: number, minMonth: number, maxMonth: number, minDay: number, maxDay: number) {
  if (year === minYear && month === minMonth) return range(minDay, new Date(year, month, 0).getDate());
  if (year === maxYear && month === maxMonth) return range(1, maxDay);
  return range(1, new Date(year, month, 0).getDate());
}

function determineDate(from: {year: number, month: number, day: number}, to: {year: number, month: number, day: number}, minDate: string | Date, maxDate: string | Date) {
  const {year: minYear, month: minMonth, day: minDay} = toFragment(minDate);
  const {year: maxYear, month: maxMonth, day: maxDay} = toFragment(maxDate);
  const m = normalize(to.month, getMonthRange(to.year, minYear, maxYear, minMonth, maxMonth), to.year > from.year);
  const d = normalize(to.day, getDayRange(to.year, m, minYear, maxYear, minMonth, maxMonth, minDay, maxDay), m > from.month);
  return toDateString(to.year, m, d);
}

function normalize(value: number, range: number[], ahead: boolean) {
  if (range.length === 0) return value;
  if (range.includes(value)) return value;
  return ahead ? range[0] : range[range.length - 1];
}
